import { Injectable } from '@angular/core';

import { Observable, catchError, filter, map, of, shareReplay, switchMap, take } from 'rxjs';

import { CurationAgentsApiService } from '../../api/agents/curation-agents.api.service';
import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../Core/Users/enums/user-permissions.enum';
import { CurationAgentModel } from '../models/curation-agent.model';

/**
 * The account's AI agents, for the places that offer one as a mention source - briefings, alerts
 * and reports. Provided in root because those pickers live outside the Automation Agents module,
 * which is where CurationAgentsService is provided.
 *
 * Empty for accounts without the agentic feature, so the pickers have nothing to list.
 */
@Injectable({ providedIn: 'root' })
export class AiAgentsListService {

    readonly all$: Observable<CurationAgentModel[]> = this.currentUserStateService.getAccount().pipe(
        filter(account => !!account),
        take(1),
        switchMap(account => account.permissions?.includes(userPermissionsEnum.agenticFeature)
            // Shared by every source picker, so a failed request must not error the replay - the
            // pickers would sit on their loading state. It just leaves the AI agent type out.
            ? this.curationAgentsApi.list().pipe(catchError(() => of([])))
            : of([])
        ),
        map(agents => (agents ?? [])
            .map(agent => new CurationAgentModel(agent))
            .sort((a, b) => a.name.localeCompare(b.name))
        ),
        shareReplay(1)
    );

    constructor(
        private curationAgentsApi: CurationAgentsApiService,
        private currentUserStateService: CurrentUserStateService
    ) {}
}
